<template>
  <div class="seo-audit">
    <div class="score-wrap">
      <div class="score-card">
        <span class="text-muted">Current Score</span>
        <div class="score">
          <span class="current">{{ reportData?.analysis_content?.score || 0 }}</span> / <span>100</span>
        </div>
        <div class="progress">
          <div
            class="progress-bar bg-success"
            role="progressbar"
            :style="{width: (reportData?.analysis_content?.score ?? 0) + '%'}"
            :aria-valuenow="reportData?.analysis_content?.score ?? 0"
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
      </div>

      <div class="score-card">
        <span class="text-muted">Issues Found</span>
        <p class="issues-count">
          {{ issuesCountBySeverity.total ?? '-' }}
        </p>
        <div class="badges-list">
          <div
            v-if="issuesCountBySeverity.critical"
            class="badge badge-danger"
          >
            {{ issuesCountBySeverity.critical }} Critical
          </div>
          <div
            v-if="issuesCountBySeverity.warning"
            class="badge badge-warning"
          >
            {{ issuesCountBySeverity.warning }} Warning
          </div>
          <div
            v-if="issuesCountBySeverity.info"
            class="badge badge-info"
          >
            {{ issuesCountBySeverity.info }} Info
          </div>
        </div>
      </div>

      <div class="score-card">
        <span class="text-muted">Potential Gain</span>
        <div class="gains-card">
          <div class="gains-item">
            <span>+ {{ reportData?.analysis_content?.potential_gain ?? '-' }}</span> points
          </div>
          <span class="text-muted">After optimization</span>
        </div>
      </div>
    </div>

    <!-- NEW: Executive Summary / Quantitative Analysis / Recommendations -->
    <div class="analysis-v2 mt-3">
      <!-- Executive Summary -->
      <div
        v-if="analysisExecutiveSummary.length"
        class="panel-card collapsible-panel"
      >
        <div
          class="panel-head clickable"
          @click="isExecutiveSummaryExpanded = !isExecutiveSummaryExpanded"
        >
          <div class="panel-head-content">
            <div class="panel-title">
              Executive Summary
            </div>
            <span class="panel-sub text-muted">
              Key takeaways prioritized by severity
            </span>
          </div>
          <i
            class="bi panel-chevron"
            :class="isExecutiveSummaryExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          />
        </div>

        <div
          v-show="isExecutiveSummaryExpanded"
          class="panel-body"
        >
          <ul class="exec-list mb-0">
            <li
              v-for="(row, i) in analysisExecutiveSummary"
              :key="'exec-'+i"
              class="exec-item"
            >
              <span
                class="severity-badge"
                :class="severityBadgeClass(row.severity)"
              >
                {{ row.severity }}
              </span>
              <span class="exec-text">
                {{ row.text }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Quantitative Analysis -->
      <div
        v-if="hasQuantitativeAnalysis"
        class="panel-card collapsible-panel mt-3"
      >
        <div
          class="panel-head clickable"
          @click="isQuantitativeAnalysisExpanded = !isQuantitativeAnalysisExpanded"
        >
          <div class="panel-head-content">
            <div class="panel-title">
              Quantitative Analysis
            </div>
            <span class="panel-sub text-muted">
              Retailer rules and structural guidelines only
            </span>
          </div>
          <i
            class="bi panel-chevron"
            :class="isQuantitativeAnalysisExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          />
        </div>

        <div
          v-show="isQuantitativeAnalysisExpanded"
          class="panel-body"
        >
          <table style="width: 100%; border-collapse: collapse; background: #fff;">
            <thead>
              <tr style="background: #f8fafc;">
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb;">
                  Section
                </th>
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb;">
                  Metric
                </th>
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb;">
                  Actual
                </th>
                <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb;">
                  Recommended
                </th>
                <th style="padding: 12px; text-align: right; border-bottom: 1px solid #e5e7eb;">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in quantitativeRows"
                :key="'qa-'+i"
                style="border-bottom: 1px solid #e5e7eb;"
              >
                <td style="padding: 12px; font-weight: 600;">
                  {{ row.section }}
                </td>
                <td style="padding: 12px;">
                  {{ row.metric }}
                </td>
                <td style="padding: 12px;">
                  {{ row.actual }}
                </td>
                <td style="padding: 12px; color: #64748b;">
                  {{ row.recommended }}
                </td>
                <td style="padding: 12px; text-align: right;">
                  <span
                    style="padding: 4px 10px; border-radius: 12px; font-size: 12px;"
                    :style="{
                      background: row.status === 'pass' ? '#dcfce7' : row.status === 'fail' ? '#fee2e2' : '#fef3c7',
                      color: row.status === 'pass' ? '#166534' : row.status === 'fail' ? '#991b1b' : '#92400e'
                    }"
                  >
                    {{ row.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="reportData?.analysis_content?.recommendations"
        class="panel-card collapsible-panel mt-3 recommendations-panel"
      >
        <div
          class="panel-head clickable"
          @click="isRecommendationsExpanded = !isRecommendationsExpanded"
        >
          <div class="panel-head-content">
            <div class="panel-title">
              {{ reportData?.analysis_content?.recommendations_title || 'Recommendations' }}
            </div>
            <span
              v-if="reportData?.analysis_content?.recommendations_description"
              class="panel-sub text-muted"
            >
              {{ reportData.analysis_content.recommendations_description }}
            </span>
          </div>
          <i
            class="bi panel-chevron"
            :class="isRecommendationsExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          />
        </div>

        <div
          v-show="isRecommendationsExpanded"
          class="panel-body recommendations-body"
        >
          <div
            v-for="(section, sectionKey) in reportData.analysis_content.recommendations"
            :key="sectionKey"
            class="recommendation-section"
          >
            <h6 class="recommendation-section-title">
              {{ formatSectionTitle(sectionKey) }}
            </h6>

            <div class="recommendation-table-wrap">
              <table class="recommendation-table">
                <thead>
                  <tr>
                    <th class="col-issue">
                      Issue
                    </th>
                    <th class="col-actual">
                      Actual vs. Recommended
                    </th>
                    <th class="col-recommendation">
                      Recommendation
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, i) in section.rows"
                    :key="i"
                  >
                    <td class="col-issue">
                      {{ row.issue }}
                    </td>
                    <td class="col-actual">
                      {{ row.actual_vs_recommended }}
                    </td>
                    <td class="col-recommendation">
                      {{ row.recommendation }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="section.recommended_keywords?.length"
              class="recommended-keywords"
            >
              <span class="keywords-label">{{ section.recommended_keywords_label || 'Recommended keywords:' }}</span>
              <div class="keywords-list">
                <span
                  v-for="(keyword, ki) in section.recommended_keywords"
                  :key="ki"
                  class="keyword-pill"
                >
                  {{ keyword }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="analysis-v2 mt-3">
      <div
        v-if="reportData?.analysis_content?.issues?.length"
        class="panel-card collapsible-panel"
      >
        <div
          class="panel-head clickable"
          @click="isSummaryExpanded = !isSummaryExpanded"
        >
          <div class="panel-head-content">
            <div class="panel-title">
              {{ reportData?.analysis_content?.summary_title || 'Summary' }}
            </div>
            <span
              v-if="reportData?.analysis_content?.summary_description"
              class="panel-sub text-muted"
            >
              {{ reportData.analysis_content.summary_description }}
            </span>
          </div>
          <i
            class="bi panel-chevron"
            :class="isSummaryExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          />
        </div>

        <div
          v-show="isSummaryExpanded"
          class="panel-body"
        >
          <div class="issues-list">
            <div
              v-for="(issue, idx) in reportData?.analysis_content?.issues || []"
              :key="'issue-'+idx"
              class="issue-item"
              :class="{
                'is-critical': issue?.severity === 'critical',
                'is-warning': issue?.severity === 'warning',
                'is-info': issue?.severity === 'info'
              }"
            >
              <div class="issue-main">
                <div class="issue-title-line">
                  <div class="issue-title">
                    {{ issue?.title }}
                  </div>
                  <div
                    v-if="issue?.category"
                    class="pill pill-light ms-2"
                  >
                    {{ issue.category }}
                  </div>
                </div>
                <div
                  v-if="issue?.explanation"
                  class="issue-subtitle text-muted"
                >
                  {{ issue.explanation }}
                </div>
                <div
                  v-if="issue?.impact"
                  class="issue-details"
                >
                  <div
                    class="detail text-muted"
                  >
                    {{ issue.impact }}
                  </div>
                </div>
              </div>
              <div class="issue-side">
                <span
                  class="severity-badge"
                  :class="{
                    'badge-danger': issue?.severity === 'critical',
                    'badge-warning': issue?.severity === 'warning',
                    'badge-info': issue?.severity === 'info'
                  }"
                >
                  {{ issue?.severity }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DefaultTemplate",
  props: {
    reportData: {
      type: Object,
      default: null
    },
    analysisContent: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isExecutiveSummaryExpanded: false,
      isQuantitativeAnalysisExpanded: false,
      isRecommendationsExpanded: false,
      isSummaryExpanded: false,
    }
  },

  computed: {
    issuesCountBySeverity() {
      const issues = Array.isArray(this.reportData?.analysis_content?.issues)
        ? this.reportData.analysis_content.issues
        : [];

      return {
        total: issues.length,
        critical: issues.filter(i => i?.severity === 'critical').length,
        warning: issues.filter(i => i?.severity === 'warning').length,
        info: issues.filter(i => i?.severity === 'info').length,
      };
    },
    analysisExecutiveSummary() {
      const list = this.analysisContent?.executive_summary;
      return Array.isArray(list) ? list.filter(x => x && x.text) : [];
    },

    hasQuantitativeAnalysis() {
      const qa = this.analysisContent?.quantitative_analysis;
      return qa && typeof qa === 'object';
    },

    quantitativeRows() {
      const qa = this.analysisContent?.quantitative_analysis || {};
      const rows = [];

      const push = (section, metric, node, fallbackRecommended = '') => {
        if (!node || typeof node !== 'object') return;
        const actual = node.actual ?? null;
        const recommended = node.recommended ?? fallbackRecommended;
        const status = node.status ?? null;
        if (actual == null && !recommended && !status) return;

        rows.push({
          section,
          metric,
          actual: actual == null ? '-' : actual,
          recommended: recommended || '-',
          status: status || 'warning'
        });
      };

      // Title
      push('Title', 'Character length', qa?.title?.character_length, 'By marketplace');

      // Bullets
      push('Bullets', 'Bullet count', qa?.bullets?.bullet_count, 5);
      push('Bullets', 'Average bullet length', qa?.bullets?.average_bullet_length, '150–200');

      // Description
      push('Product Description', 'Character length', qa?.description?.character_length, '800–2000');
      push('Product Description', 'Paragraph structure', qa?.description?.paragraph_count, 'multiple');
      push('Product Description', 'Duplicate content', qa?.description?.duplicate_sentence_count, 0);

      return rows;
    },

    hasRecommendations() {
      const recs = this.analysisContent?.recommendations;
      return recs && typeof recs === 'object' && Object.keys(recs).length > 0;
    },

    recommendationsBlocks() {
      const recs = this.analysisContent?.recommendations || {};
      const normalize = (v) => Array.isArray(v) ? v.filter(Boolean) : [];

      return [
        { key: 'title', title: 'Title', items: normalize(recs.title) },
        { key: 'bullets', title: 'Bullets', items: normalize(recs.bullets) },
        { key: 'description', title: 'Description', items: normalize(recs.description) },
        { key: 'geo_optimization', title: 'GEO Optimization', items: normalize(recs.geo_optimization) }
      ];
    },
  },

  methods: {
    severityBadgeClass(severity) {
      return {
        'badge-danger': severity === 'critical',
        'badge-warning': severity === 'warning',
        'badge-info': severity === 'info'
      };
    },
    formatSectionTitle(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
  }
}
</script>

<style scoped lang="scss">
.seo-audit {
  .score-wrap {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 20px;
  }

  .score-card {
    background: #fff;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: .75rem;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .score {
    font-size: 28px;
    font-weight: 700;
    .current { color: #111827 }
  }

  .issues-count {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
  }

  .badges-list {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    .badge { border-radius: 999px; padding: 6px 10px; font-weight: 600 }
  }

  .gains-card {
    display: flex;
    flex-direction: column;
    .gains-item {
      font-size: 28px;
      font-weight: 700;
      color: #16a34a;
      line-height: 1;
      margin-bottom: 4px;
    }
  }

  .score-body {
    background: #fff;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: .75rem;
    padding: 16px;
  }

  .issues-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .issue-item {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: .75rem;
    align-items: start;
    &.is-critical { border-color: #fecaca }
    &.is-warning { border-color: #fde68a }
    &.is-info { border-color: #bfdbfe }
  }

  .issue-main {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .issue-title-line {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .issue-title {
    font-weight: 700;
    color: #111827;
  }

  .pill {
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 600;
    &.pill-light {
      background: #f3f4f6;
      color: #374151;
      border: 1px solid #e5e7eb;
    }
  }

  .issue-subtitle {
    font-size: 14px;
  }

  .issue-details {
    display: grid;
    gap: 4px;
    .detail { font-size: 13px }
  }

  .issue-side {
    display: flex;
    align-items: center;
  }

  .severity-badge {
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 12px;
    text-transform: capitalize;
    font-weight: 700;
  }

  .badge-danger { background: #fee2e2; color: #b91c1c }
  .badge-warning { background: #fef3c7; color: #92400e }
  .badge-info { background: #dbeafe; color: #1e40af }

  @media (max-width: 991.98px) {
    .score-wrap { grid-template-columns: 1fr }
  }

  .analysis-v2 {
    .panel-card {
      background: #fff;
      border: 1px solid var(--bs-border-color, #e5e7eb);
      border-radius: .75rem;
      overflow: hidden;
    }

    .panel-head {
      padding: 14px 16px;
      border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
      background: #f8fafc;
      display: flex;
      flex-direction: column;
      gap: 2px;

      &.clickable {
        cursor: pointer;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        user-select: none;
        transition: background-color 0.15s ease;

        &:hover {
          background: #f1f5f9;
        }
      }
    }

    .panel-head-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .panel-chevron {
      font-size: 18px;
      color: #6b7280;
      transition: transform 0.2s ease;
    }

    .collapsible-panel .panel-head:not(.clickable) + .panel-body,
    .collapsible-panel .panel-body {
      transition: max-height 0.3s ease;
    }

    .panel-title {
      font-weight: 800;
      color: #111827;
      font-size: 16px;
      line-height: 1.2;
    }

    .panel-sub {
      font-size: 13px;
    }

    .panel-body {
      padding: 14px 16px;
    }

    .exec-list {
      padding-left: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .exec-item {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
      align-items: start;
    }

    .exec-text {
      font-size: 14px;
      color: #111827;
      line-height: 1.4;
    }

    .qa-table {
      th {
        font-weight: 700;
        color: #6b7280;
        border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
        background: #fff;
        white-space: nowrap;
      }
      td {
        vertical-align: middle;
        border-top: 1px solid var(--bs-border-color, #eef0f3);
      }
    }

    .status-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      padding: 6px 10px;
      font-size: 12px;
      font-weight: 800;
      text-transform: capitalize;
      border: 1px solid transparent;

      &.is-pass {
        background: #d1fae5;
        color: #065f46;
        border-color: #a7f3d0;
      }
      &.is-warning {
        background: #fef3c7;
        color: #92400e;
        border-color: #fde68a;
      }
      &.is-fail {
        background: #fee2e2;
        color: #b91c1c;
        border-color: #fecaca;
      }
    }

    .recs-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;

      @media (max-width: 991.98px) {
        grid-template-columns: 1fr;
      }
    }

    .recs-card {
      border: 1px solid var(--bs-border-color, #e5e7eb);
      border-radius: .75rem;
      padding: 12px 12px;
      background: #fff;
    }

    .recs-title {
      font-weight: 800;
      color: #111827;
      margin-bottom: 8px;
    }

    .recs-list {
      padding-left: 18px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 14px;
      color: #111827;
    }

    .recs-item {
      line-height: 1.35;
    }
  }
}
</style>
